<!-- Show the thumbnail until it's fully loaded, and until the video plays: the player stays
     hidden till then (2026-10-22, Seán, issue T3), so a visitor Vimeo's bot check refuses (a VPN,
     an automated browser) sees the still, not "We couldn't verify the security of your
     connection" -->
<!-- You must pass either a valid element or a valid id -->
<!-- <figure v-if="id"> is just a safeguard -->

<template>
  <figure v-if="id" @mouseover="replayVideo" @click="replayVideo">
    <div class="wrapper">
      <img :src="thumbnailUrl" class="thumbnail" @load="createPlayer" />
      <div id="vimeo-container" :class="{isPlaying}" />
    </div>
    <figcaption>
      <small v-if="credit" class="credit">&#169; {{ credit }}</small>
    </figcaption>
  </figure>
</template>

<script setup>
const log = (msg) => process.env.VUE_APP_DEV && console.log(`Vimeo: ${msg}`);
const props = defineProps(["id", "credit"]);
import {ref, onMounted} from "vue";
import Player from "@vimeo/player";
let player;

// State variables
const thumbnailUrl = ref(""); // Store the thumbnail URL
const isPlaying = ref(false); // the player shown: it has really started (Vimeo's "playing")

// Fetch the video thumbnail from Vimeo API.
const fetchThumbnail = async () => {
  try {
    const apiUrl = `https://vimeo.com/api/v2/video/${props.id}.json`;
    log(`Fetching Vimeo thumbnail from: ${apiUrl}`);
    const response = await fetch(apiUrl, {mode: "cors"});
    // Detect if the request failed
    if (!response.ok) {
      throw new Error(`Failed to fetch thumbnail: HTTP ${response.status}`);
    }
    // Parse response
    const data = await response.json();
    // Log the raw response
    log("Vimeo API Response:", data);
    // Validate and set the thumbnail URL
    if (
      data &&
      Array.isArray(data) &&
      data.length > 0 &&
      typeof data[0].thumbnail_large === "string"
    ) {
      const imageUrl = data[0].thumbnail_large;
      // Extra validation: Ensure it's a valid image URL
      if (imageUrl.startsWith("http")) {
        thumbnailUrl.value = imageUrl;
        log(`Thumbnail set: ${thumbnailUrl.value}`);
      } else {
        throw new Error("Invalid image URL format.");
      }
    } else {
      throw new Error("Thumbnail data missing or in unexpected format.");
    }
  } catch (error) {
    log(`Error fetching thumbnail: ${error.message}`);
    // Set a fallback image or clear the thumbnail to prevent app homepage from showing
    thumbnailUrl.value = "";
  }
};

/* After play, reset the iframe while keeping the thumbnail visible.*/

const RETRY_DELAY_MS = 2000;
let hasRetried = false;

// Vimeo's embed lookup sometimes fails ("There was an error fetching the embed code from
// Vimeo"; tests, 2026-10-19): tried once more; then the thumbnail stays, which is all a visitor
// sees anyway, so it's logged, not an error
function retryOnce(error) {
  log(`player not ready: ${error?.message || error}`);
  if (hasRetried) return;
  hasRetried = true;
  player?.destroy?.().catch(() => {});
  player = undefined;
  setTimeout(createPlayer, RETRY_DELAY_MS);
}

/*  Creates the Vimeo player inside the container. */
const createPlayer = () => {
  if (player) return;
  const origin = window.location.origin; // e.g. https://localhost:9000
  const options = {
    id: props.id,
    autoplay: true,
    muted: true,
    background: false,
    controls: false,
    loop: false,
    responsive: true,
    url: `https://player.vimeo.com/video/${props.id}?origin=${encodeURIComponent(origin)}`
  };
  player = new Player("vimeo-container", options);
  player.on("playing", () => (isPlaying.value = true));
  player
    .ready()
    .then(() => {
      log("Vimeo Player is ready!");
    })
    .catch(retryOnce);

  log("Vimeo player initialized.");
};

/** Initialize the component on mount. */
// onMounted(async () => await fetchThumbnail());

onMounted(async () => {
  log(`onMounted() called with props.id = ${props.id}`);
  await fetchThumbnail();
});

// upon click or hover, replay the video
async function replayVideo() {
  log("replayVideo()");
  if (!player) return; // protect if "can't access property "getPaused", player is undefined"
  player
    .getPaused()
    .then((paused) => {
      if (paused) {
        log("Starting video replay");
        player.play(); // restart start playback
      } else {
        log("Video is currently playing.");
      }
    })
    .catch((error) => {
      console.error("Error checking play state:", error);
    });
}
</script>

<style scoped>
figure {
  width: 100%;
  margin: 0;
  display: block;
}

.wrapper {
  width: 100%;
  position: relative;
  border: none;
  padding-top: 56.25%; /* 16:9 aspect ratio */
  overflow: hidden;
}

@keyframes fadeEffect {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0.4;
  }
}

/* Display the thumbnail image */
img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 2; /* Position above <div class="wrapper"> but below <div id="vimeo-container"> */
  opacity: 1;
  border: none;
  transition: opacity 0.3s ease-in-out; /* Smooth transition */
}

/* Display the video, once it plays (till then the thumbnail; isPlaying) */
#vimeo-container {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease-in-out;
  z-index: 10;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
#vimeo-container.isPlaying {
  opacity: 1;
  pointer-events: auto;
}

figcaption {
  margin-top: 8px;
  text-align: left;
  font-size: 0.9rem;
  color: #666;
}

.credit {
  display: block;
  margin-bottom: 4px;
  font-size: 0.8rem;
  color: #888;
}
</style>
